import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import {
  ArrowLeft,
  Plus,
  Search,
  Lightbulb,
  User,
  Building,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useDebounce } from '@/hooks/use-debounce';
import {
  createTicket,
  fetchProviders,
  createProvider,
  searchSolutions,
  updateTicketTags,
  type SupportTicket,
  type EquipmentProvider,
  type SimilarSolution,
  type TicketTag,
} from '@/lib/api/support-tickets';
import { TicketTagInput } from '@/components/support/TicketTagInput';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const CATEGORY_OPTIONS = [
  { value: 'system_offline', label: 'System Offline' },
  { value: 'performance', label: 'Performance Issue' },
  { value: 'hardware', label: 'Hardware Problem' },
  { value: 'billing', label: 'Billing Query' },
  { value: 'monitoring', label: 'Monitoring Issue' },
  { value: 'other', label: 'Other' },
];

function SuggestionCard({
  suggestion,
  onClick,
}: {
  suggestion: SimilarSolution;
  onClick: () => void;
}) {
  return (
    <Card
      className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 cursor-pointer hover:border-blue-400"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <span className="font-medium">{suggestion.ticketNumber}</span>
          <Badge variant="outline">{suggestion.similarity}% match</Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
          {suggestion.issueDescription}
        </p>
        {suggestion.resolution && (
          <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded-md">
            <p className="text-xs font-medium text-green-600 mb-1">Resolution:</p>
            <p className="text-sm line-clamp-3">{suggestion.resolution}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SupportTicketCreatePage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [customerType, setCustomerType] = useState<'linked' | 'standalone'>('standalone');
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [showNewProviderDialog, setShowNewProviderDialog] = useState(false);
  const [newProviderName, setNewProviderName] = useState('');
  const [selectedTags, setSelectedTags] = useState<TicketTag[]>([]);

  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    systemProvider: '',
    providerId: null as number | null,
    issueDescription: '',
    issueCategory: '',
    priority: 'normal',
  });

  const debouncedIssue = useDebounce(formData.issueDescription, 500);

  // Fetch providers
  const { data: providers = [] } = useQuery({
    queryKey: ['equipment-providers'],
    queryFn: () => fetchProviders(true),
  });

  // Search customers (only when linked mode)
  const { data: customers = [] } = useQuery({
    queryKey: ['customers-search', customerSearch],
    queryFn: async () => {
      if (!customerSearch || customerSearch.length < 2) return [];
      const response = await fetch(
        `/api/customers?search=${encodeURIComponent(customerSearch)}&limit=10`,
        { credentials: 'include' }
      );
      return response.json();
    },
    enabled: customerType === 'linked' && customerSearch.length >= 2,
  });

  // AI suggestions based on issue description
  const { data: suggestionsData, isLoading: loadingSuggestions } = useQuery({
    queryKey: ['ticket-create-suggestions', debouncedIssue, formData.systemProvider],
    queryFn: () => searchSolutions(debouncedIssue, formData.systemProvider || undefined),
    enabled: debouncedIssue.length > 20,
  });

  // Create ticket mutation
  const createMutation = useMutation({
    mutationFn: async (ticketData: Partial<SupportTicket>) => {
      const ticket = await createTicket(ticketData);
      // Set tags if any selected
      if (selectedTags.length > 0) {
        await updateTicketTags(ticket.id, selectedTags.map((t) => t.id));
      }
      return ticket;
    },
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['support-ticket-stats'] });
      toast({ title: `Ticket ${ticket.ticketNumber} created` });
      setLocation(`/support-tickets/${ticket.id}`);
    },
    onError: () => {
      toast({ title: 'Failed to create ticket', variant: 'destructive' });
    },
  });

  // Create provider mutation
  const createProviderMutation = useMutation({
    mutationFn: createProvider,
    onSuccess: (provider) => {
      queryClient.invalidateQueries({ queryKey: ['equipment-providers'] });
      setFormData({ ...formData, systemProvider: provider.name, providerId: provider.id });
      setShowNewProviderDialog(false);
      setNewProviderName('');
      toast({ title: `Provider "${provider.name}" added` });
    },
    onError: () => {
      toast({ title: 'Failed to create provider', variant: 'destructive' });
    },
  });

  const handleSubmit = () => {
    if (!formData.issueDescription.trim()) {
      toast({ title: 'Please enter an issue description', variant: 'destructive' });
      return;
    }

    const ticketData: Partial<SupportTicket> = {
      issueDescription: formData.issueDescription,
      issueCategory: formData.issueCategory || null,
      systemProvider: formData.systemProvider || null,
      providerId: formData.providerId || null,
      priority: formData.priority,
    };

    if (customerType === 'linked' && selectedCustomer) {
      ticketData.customerId = selectedCustomer.id;
    } else {
      ticketData.customerName = formData.customerName || null;
      ticketData.customerPhone = formData.customerPhone || null;
      ticketData.customerEmail = formData.customerEmail || null;
      ticketData.customerAddress = formData.customerAddress || null;
    }

    createMutation.mutate(ticketData);
  };

  const handleSelectCustomer = (customer: any) => {
    setSelectedCustomer(customer);
    setCustomerSearch('');
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setLocation('/support-tickets')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Create Support Ticket</h1>
          <p className="text-muted-foreground">
            Log a new customer support issue
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <RadioGroup
                value={customerType}
                onValueChange={(val) => {
                  setCustomerType(val as 'linked' | 'standalone');
                  setSelectedCustomer(null);
                }}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="standalone" id="standalone" />
                  <Label htmlFor="standalone">Enter Details Manually</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="linked" id="linked" />
                  <Label htmlFor="linked">Link to Existing Customer</Label>
                </div>
              </RadioGroup>

              {customerType === 'linked' ? (
                <div className="space-y-4">
                  {selectedCustomer ? (
                    <div className="p-4 bg-muted rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{selectedCustomer.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {selectedCustomer.email || selectedCustomer.phone || 'No contact info'}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedCustomer(null)}
                        >
                          Change
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search customers by name, email, or phone..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="pl-10"
                      />
                      {customers.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-popover border rounded-lg shadow-lg max-h-60 overflow-auto">
                          {customers.map((customer: any) => (
                            <div
                              key={customer.id}
                              className="p-3 hover:bg-muted cursor-pointer"
                              onClick={() => handleSelectCustomer(customer)}
                            >
                              <p className="font-medium">{customer.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {customer.email || customer.phone}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Customer Name</Label>
                    <Input
                      value={formData.customerName}
                      onChange={(e) =>
                        setFormData({ ...formData, customerName: e.target.value })
                      }
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.customerPhone}
                      onChange={(e) =>
                        setFormData({ ...formData, customerPhone: e.target.value })
                      }
                      placeholder="07xxx xxxxxx"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) =>
                        setFormData({ ...formData, customerEmail: e.target.value })
                      }
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input
                      value={formData.customerAddress}
                      onChange={(e) =>
                        setFormData({ ...formData, customerAddress: e.target.value })
                      }
                      placeholder="123 Main Street"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Issue Details */}
          <Card>
            <CardHeader>
              <CardTitle>Issue Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Equipment Provider</Label>
                  <div className="flex gap-2 mt-1">
                    <Select
                      value={formData.systemProvider}
                      onValueChange={(providerName) => {
                        const provider = providers.find(p => p.name === providerName);
                        setFormData({
                          ...formData,
                          systemProvider: providerName,
                          providerId: provider?.id || null
                        });
                      }}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {providers.map((provider) => (
                          <SelectItem key={provider.id} value={provider.name}>
                            {provider.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowNewProviderDialog(true)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select
                    value={formData.issueCategory}
                    onValueChange={(val) =>
                      setFormData({ ...formData, issueCategory: val })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Issue Description *</Label>
                <Textarea
                  value={formData.issueDescription}
                  onChange={(e) =>
                    setFormData({ ...formData, issueDescription: e.target.value })
                  }
                  placeholder="Describe the issue in detail..."
                  rows={5}
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Tip: A detailed description helps find similar past issues
                </p>
              </div>

              <div>
                <Label>Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(val) =>
                    setFormData({ ...formData, priority: val })
                  }
                >
                  <SelectTrigger className="mt-1 w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Tags</Label>
                <div className="mt-1">
                  <TicketTagInput
                    selectedTags={selectedTags}
                    onTagsChange={setSelectedTags}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              variant="outline"
              onClick={() => setLocation('/support-tickets')}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Create Ticket
            </Button>
          </div>
        </div>

        {/* AI Suggestions Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Similar Past Issues
              </CardTitle>
              <CardDescription>
                {debouncedIssue.length < 20
                  ? 'Enter more details to find similar issues'
                  : suggestionsData?.aiAvailable
                  ? 'AI-powered suggestions'
                  : 'Keyword-based matches'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSuggestions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : debouncedIssue.length < 20 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Enter at least 20 characters in the issue description to see
                  similar past tickets
                </p>
              ) : suggestionsData?.suggestions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No similar past issues found
                </p>
              ) : (
                <div className="space-y-4">
                  {suggestionsData?.suggestions.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.ticketId}
                      suggestion={suggestion}
                      onClick={() =>
                        window.open(
                          `/support-tickets/${suggestion.ticketId}`,
                          '_blank'
                        )
                      }
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* New Provider Dialog */}
      <Dialog open={showNewProviderDialog} onOpenChange={setShowNewProviderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Provider</DialogTitle>
            <DialogDescription>
              Add a new equipment provider to the list
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Provider Name</Label>
            <Input
              value={newProviderName}
              onChange={(e) => setNewProviderName(e.target.value)}
              placeholder="e.g., SunPower, Enphase"
              className="mt-1"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNewProviderDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                createProviderMutation.mutate({ name: newProviderName })
              }
              disabled={!newProviderName.trim() || createProviderMutation.isPending}
            >
              Add Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
